import axios from 'axios';
import { API_BASE_URL } from './apiConfig';

const API_ROOT = `${API_BASE_URL}/api`;

/**
 * Get global competitiveness data with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.year - Filter by year (optional)
 * @param {string[]} params.countries - Array of country names (optional)
 * @param {string[]} params.minerals - Array of mineral names (optional)
 * @returns {Promise} - API response with competitiveness data
 */
export const getCompetitivenessData = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.year) {
      queryParams.append('year', params.year);
    }
    
    if (params.countries && params.countries.length > 0) {
      queryParams.append('countries', params.countries.join(','));
    }
    
    if (params.minerals && params.minerals.length > 0) {
      queryParams.append('minerals', params.minerals.join(','));
    }
    
    const url = `${API_ROOT}/global-competitiveness${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await axios.get(url);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching competitiveness data:', error);
    throw error;
  }
};

/**
 * Get summary statistics for global competitiveness
 * @param {number} year - Filter by year (optional)
 * @returns {Promise} - API response with summaries and KPIs
 */
export const getCompetitivenessSummary = async (year = null) => {
  try {
    const url = year 
      ? `${API_ROOT}/global-competitiveness/summary?year=${year}`
      : `${API_ROOT}/global-competitiveness/summary`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching competitiveness summary:', error);
    throw error;
  }
};

/**
 * Add or update mineral self-reliance data
 * @param {Object} data - Mineral data to add
 * @param {string} data.country - Country name
 * @param {string} data.mineral - Mineral name
 * @param {number} data.year - Year of data
 * @param {number} data.production_t - Production in tonnes
 * @param {number} data.imports_t - Import volume in tonnes
 * @param {number} data.exports_t - Export volume in tonnes
 * @param {string} data.data_source - Data source reference (optional)
 * @returns {Promise} - API response with success status
 */
export const addCompetitivenessData = async (data) => {
  try {
    const formData = new FormData();
    formData.append('country', data.country);
    formData.append('mineral', data.mineral);
    formData.append('year', data.year);
    formData.append('production_t', data.production_t);
    formData.append('imports_t', data.imports_t);
    formData.append('exports_t', data.exports_t);
    
    if (data.data_source) {
      formData.append('data_source', data.data_source);
    }
    
    const response = await axios.post(`${API_ROOT}/global-competitiveness/add`, formData);
    return response.data;
  } catch (error) {
    console.error('Error adding competitiveness data:', error);
    throw error;
  }
};

/**
 * Get available years in the dataset
 * @returns {Promise} - API response with available years
 */
export const getAvailableYears = async () => {
  try {
    const response = await axios.get(`${API_ROOT}/global-competitiveness/years`);
    return response.data;
  } catch (error) {
    console.error('Error fetching available years:', error);
    throw error;
  }
};

const competitivenessApi = {
  getCompetitivenessData,
  getCompetitivenessSummary,
  addCompetitivenessData,
  getAvailableYears,
};

export default competitivenessApi;

